import { dedupeLotsList, isLotAvailableForSale, resolveLotNumber } from './lotSystem';
import { getProductDisplaySellingPrice, getCachedLots } from '../services/cacheReadModel';
import { canonicalProductId } from './productIdUtils';
import { getSaleLineUnitPrice } from '../domain/sales/saleLineMath';
import { lotBelongsToStore, DEFAULT_STORE_ID } from '../domain/inventory/stores/storeCatalog';

function normalizeLotsList(lots) {
  if (!lots) return [];
  return Array.isArray(lots) ? lots : Object.values(lots);
}

function sortLotsNewestFirst(lots) {
  return [...lots].sort(
    (a, b) =>
      new Date(b.purchaseDate || b.createdAt || 0) -
      new Date(a.purchaseDate || a.createdAt || 0)
  );
}

function priceKey(mro) {
  return String(Math.round(Number(mro) * 100) / 100);
}

function resolveLotId(productOrLine) {
  return (
    productOrLine?.lotId ||
    productOrLine?.priceVariantLot?.id ||
    productOrLine?.lot?.id ||
    productOrLine?.batch?.id ||
    null
  );
}

/**
 * POS card identity for in-cart highlight.
 * Lot cards: `${productId}_lot_${lotId}` — one card per lot.
 * Plain cards: `${productId}`.
 */
export function resolvePosCardKey(product) {
  if (product?.posCardKey) return String(product.posCardKey);
  const productId = product?.id ?? product?.productId;
  if (productId == null || productId === '') return '';
  const lotId = resolveLotId(product);
  if (lotId != null && lotId !== '') {
    return `${productId}_lot_${lotId}`;
  }
  const display = Number(product?.displayPriceMro);
  if (Number.isFinite(display) && display > 0 && product?.priceVariantLot) {
    return `${productId}_vp_${priceKey(display)}`;
  }
  return String(productId);
}

/** Cart line → same key space as POS cards (by productId + lot). */
export function resolveCartLinePosCardKey(item) {
  const productId = item?.productId ?? item?.id;
  if (productId == null || productId === '') return '';
  const lotId = resolveLotId(item);
  if (lotId != null && lotId !== '') {
    return `${productId}_lot_${lotId}`;
  }
  const price = Number(item?.price);
  if (Number.isFinite(price) && price > 0) {
    return `${productId}_vp_${priceKey(price)}`;
  }
  return String(productId);
}

/**
 * Qty map keyed by posCardKey for grid selection badges.
 * Lot cards only count cart lines for that lot.
 * Plain productId keys are kept as fallback (restaurant / no-lot cards).
 */
export function buildCartQtyByPosCardKey(cart) {
  const map = Object.create(null);
  if (!Array.isArray(cart) || cart.length === 0) return map;
  for (let i = 0; i < cart.length; i += 1) {
    const item = cart[i];
    const qty = Number(item?.quantity) || 0;
    if (qty <= 0) continue;
    const productId = String(item?.productId ?? item?.id ?? '');
    if (!productId) continue;
    const lotId = resolveLotId(item);
    if (lotId != null && lotId !== '') {
      const lotKey = `${productId}_lot_${lotId}`;
      map[lotKey] = (map[lotKey] || 0) + qty;
    } else {
      const price = Number(item?.price);
      if (Number.isFinite(price) && price > 0) {
        const vpKey = `${productId}_vp_${priceKey(price)}`;
        map[vpKey] = (map[vpKey] || 0) + qty;
      }
    }
    map[productId] = (map[productId] || 0) + qty;
  }
  return map;
}

function dedupeProductsById(products) {
  const seen = new Set();
  const out = [];
  for (const product of products) {
    const id = product?.id ?? product?.productId;
    if (id == null || id === '') continue;
    const key = String(id);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(product);
  }
  return out;
}

/**
 * One POS card per active lot at the location (+ optional store scope).
 * Same product + two distinct lots → two cards (no LotSelectionModal).
 * Ghost clones are collapsed by dedupeLotsList (SSOT) — no POS-local collapse.
 */
export function expandProductsToPriceVariantCards(
  products,
  {
    selectedStockLocation = 'Principal',
    isRestaurant = false,
    lots = null,
    storeId = null
  } = {}
) {
  const list = dedupeProductsById(Array.isArray(products) ? products : []);

  if (isRestaurant) {
    return list.map((product) => ({
      ...product,
      posCardKey: String(product.id),
      displayPriceMro: getSaleLineUnitPrice(product)
    }));
  }

  const lotsList = dedupeLotsList(
    normalizeLotsList(lots != null ? lots : getCachedLots())
  );
  const location = String(selectedStockLocation || 'Principal').trim().toLowerCase();
  const wantStore =
    storeId != null && String(storeId).trim() !== '' && String(storeId) !== 'all'
      ? String(storeId)
      : null;

  // Index sellable lots once — exclude expired / unsellable / other stores
  const lotsByProductAtLocation = new Map();
  for (const lot of lotsList) {
    if (!isLotAvailableForSale(lot)) continue;
    if (String(lot.location || 'Principal').trim().toLowerCase() !== location) continue;
    if (wantStore && !lotBelongsToStore(lot, wantStore, DEFAULT_STORE_ID)) continue;
    const pid = String(lot.productId ?? '');
    if (!pid) continue;
    let bucket = lotsByProductAtLocation.get(pid);
    if (!bucket) {
      bucket = [];
      lotsByProductAtLocation.set(pid, bucket);
    }
    bucket.push(lot);
  }

  const cards = list.flatMap((product) => {
    const productId = canonicalProductId(product);
    const catalogFallback =
      Number(
        getProductDisplaySellingPrice(productId, location) ||
          getSaleLineUnitPrice(product) ||
          0
      ) || 0;

    const activeLots = lotsByProductAtLocation.get(String(productId)) || [];

    // No sellable lots → no POS card (expired stock belongs in Rapports only)
    if (activeLots.length === 0) {
      return [];
    }

    return sortLotsNewestFirst(activeLots).map((lot) => {
      const qty = Number(lot.remainingQuantity) || 0;
      const priceMro =
        Number(lot.sellingUnitPrice || lot.purchaseUnitPrice) || catalogFallback || 0;

      return {
        ...product,
        posCardKey: `${productId}_lot_${lot.id}`,
        displayPriceMro: priceMro,
        price: priceMro,
        priceVariantLot: lot,
        lot,
        lotId: lot.id,
        batch: {
          id: lot.id,
          batchNumber: lot.batchNumber,
          expirationDate: lot.expirationDate,
          quantity: qty,
          remainingQuantity: qty
        },
        priceVariantStock: qty,
        stockQty: qty,
        stock: qty,
        lotNumber: resolveLotNumber(lot) || null,
        lotBatchNumber: resolveLotNumber(lot) || null,
        lotExpirationDate: lot.expirationDate || null
      };
    });
  });

  // Same product+lot must not appear twice in the grid
  const seenKeys = new Set();
  return cards.filter((card) => {
    const key = String(card.posCardKey || card.id || '');
    if (!key || seenKeys.has(key)) return false;
    seenKeys.add(key);
    return true;
  });
}

export function resolvePosCardPriceMro(product) {
  if (product?.displayPriceMro != null && Number(product.displayPriceMro) > 0) {
    return Number(product.displayPriceMro);
  }
  return getSaleLineUnitPrice(product);
}
